import { FC as ReactFC, useEffect, useState } from 'react';

import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import isEmpty from 'lodash/isEmpty';
import BlockUi from 'react-block-ui';
import * as intl from 'react-intl-universal';
import {
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';

import Constraints from 'constants/forms/Constraints';
import { getLocalizedErrorString } from 'helpers/ErrorFormat';
import EnhancedFormikError from 'shared/components/enhanced-formik-error/EnhancedFormikError';
import ImageComponent from 'shared/components/image/ImageComponent';
import Status from 'shared/enums/Status';
import MfaDeactivateIllustration from 'shared/static/img/mfa-deactivate.svg';
import MfaDeactivateThumb from 'shared/static/img/thumbs/mfa-deactivate.png';

import styles from './mfaDeactivateModal.module.scss';
import MfaDeactivateModalProps from './MfaDeactivateModalProps';
import MfaDeactivateValidation from './MfaDeactivateValidation';

interface MfaDeactivateFormValues {
  password: string;
}

const INITIAL_STEP = 1;

const MfaDeactivateModal: ReactFC<MfaDeactivateModalProps> = (
  props: MfaDeactivateModalProps
) => {
  const { isOpen, status, onToggle, onDeactivate } = props;

  const initialValues: MfaDeactivateFormValues = {
    password: '',
  };

  const [step, setStep] = useState<number>(INITIAL_STEP);

  useEffect(() => {
    if (isOpen) {
      setStep(INITIAL_STEP);
    }
  }, [isOpen]);

  const onClosed = (): void => {
    setStep(INITIAL_STEP);
  };

  const handleDeactivate = (
    values: MfaDeactivateFormValues,
    formikHelpers: FormikHelpers<MfaDeactivateFormValues>
  ): void => {
    onDeactivate(values.password);
  };

  /**
   * Renders appropriate API errors for each form field
   *
   * @param name Field name under which the error has to be displayed
   * @returns {string | null} The error message from the locale
   */
  const renderStatusError = (
    name: string,
    { form }: FieldProps<string, MfaDeactivateFormValues>
  ): string | null => {
    const { status: formStatus } = form;
    if (formStatus && formStatus.msg && !isEmpty(formStatus.fields)) {
      const fields = Object.keys(formStatus.fields);
      if (fields.includes(name)) {
        const errorCode = formStatus.fields[name];
        return intl.get(getLocalizedErrorString(String(errorCode)));
      }
    }
    return null;
  };

  const renderStepOne = (): JSX.Element => (
    <>
      <ModalBody>
        <Col xs="12">
          <Row className="d-flex flex-column align-items-center mb-3">
            <ImageComponent
              loading="eager"
              src={MfaDeactivateIllustration}
              className={`${styles.mfaDeactivateImg}`}
              alt="MFA Deactivate"
              thumb={MfaDeactivateThumb}
            />
          </Row>
          <Row className="mb-3">
            <span className="text-14-light">
              {intl.get('LBL_MFA_METHODS_DEACTIVATE_MODAL_STEP_01_DESCRIPTION')}
            </span>
          </Row>
        </Col>
      </ModalBody>
      <ModalFooter className="p-3">
        <Col xs="12" className="d-flex justify-content-between">
          <Button color="secondary" onClick={onToggle}>
            {intl.get('BTN_CANCEL')}
          </Button>

          <Button color="primary" onClick={() => setStep(2)}>
            {intl.get('BTN_MFA_DEACTIVATE')}
          </Button>
        </Col>
      </ModalFooter>
    </>
  );

  const renderStepTwo = (): JSX.Element => (
    <Formik
      initialValues={initialValues}
      validationSchema={MfaDeactivateValidation.GetValidationSchema()}
      onSubmit={handleDeactivate}
    >
      {({ dirty }): JSX.Element => (
        <Form noValidate>
          <ModalBody>
            <Col xs="12">
              <Row className="mb-3">
                <span className="text-16-bold">
                  {intl.get(
                    'LBL_MFA_METHODS_DEACTIVATE_MODAL_STEP_02_SUBTITLE'
                  )}
                </span>
              </Row>
              <Row className="mb-3">
                <span className="text-14-light">
                  {intl.get(
                    'LBL_MFA_METHODS_DEACTIVATE_MODAL_STEP_02_DESCRIPTION'
                  )}
                </span>
              </Row>
              <Row className="mb-3">
                <Col xs="12" className="p-0">
                  <FormGroup>
                    <Label htmlFor="password">
                      {intl.get('LBL_LOGIN_PASSWORD')}
                    </Label>
                    <Field
                      autoComplete="current-password"
                      type="password"
                      name="password"
                      maxLength={Constraints.MaxFieldLength}
                      tabIndex={0}
                      className="form-control"
                      placeholder={intl.get('LBL_LOGIN_PASSWORD_PLACEHOLDER')}
                    />
                    <EnhancedFormikError
                      name="password"
                      renderSecondaryError={renderStatusError}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </ModalBody>
          <ModalFooter className="p-3">
            <Col xs="12" className="d-flex justify-content-between">
              <Button color="secondary" onClick={() => setStep(1)}>
                {intl.get('BTN_MFA_BACK')}
              </Button>
              <Button
                type="submit"
                color="primary"
                tabIndex={0}
                disabled={!dirty}
                className={dirty ? '' : styles.btnDisabled}
              >
                {intl.get('BTN_MFA_DEACTIVATE')}
              </Button>
            </Col>
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      toggle={onToggle}
      backdrop="static"
      centered
      autoFocus={false}
      trapFocus
      keyboard={false}
      id="warningPromptTarget"
      onClosed={onClosed}
    >
      <BlockUi tag="div" blocking={status === Status.Loading}>
        <ModalHeader toggle={onToggle}>
          {intl.get('LBL_MFA_METHODS_DEACTIVATE_MODAL_TITLE')}
        </ModalHeader>
        {step === 1 ? renderStepOne() : renderStepTwo()}
      </BlockUi>
    </Modal>
  );
};

export default MfaDeactivateModal;
